import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboard, isForbidden } from '@cwos/api-client';
import type { dashboard as DashboardNS } from '@cwos/api-client';
import { Refused, SkeletonSet, type Column } from '@cwos/ui-admin';
import { useNav } from '../../../navigation.js';
import { useAnalyticsControls } from '../AnalyticsContext.js';
import { askPeriod, formatNumber, periodRangeLabel } from '../analyticsRange.js';
import { isWithheld, measure, withheldReason, type MeasureDisplay } from '../disclosure.js';
import { DrillPanel, WeeklyPanel, type Drill, type DrillSet, TrendFailure } from '../drilldown.js';
import { METRIC_ABOUT, ofLabel, type MetricAboutKey } from '../explain.js';
import {
  DisclosureNote,
  DisclosureStrip,
  DrilldownTable,
  MeasureStat,
  Panel,
  StatGrid,
  StalenessStrip,
  type QueryLike,
} from '../shared.js';
import { LoadFailure } from '../../../LoadFailure.js';
import { explainDraft } from '../../../intelligence/global/index.js';

type ScheduleByWorkerRow = DashboardNS.ScheduleByWorkerRow;

const count = (v: number) => formatNumber(v);

/**
 * `GET /api/dashboard/analytics/schedule` answers two audiences on one URL —
 * see the note on `StaffView`. The five shift measures suppress as ONE block
 * (they are arithmetically linked through `completionRatePct`), so when they go
 * they all go, and all five tiles say the same reason.
 */
export function ScheduleView() {
  const { apiQuery } = useAnalyticsControls();
  const navigate = useNav();
  const [drill, setDrill] = useState<Drill | null>(null);

  const schedule = useQuery({
    queryKey: ['analytics', 'schedule', apiQuery],
    queryFn: () => dashboard.analyticsSchedule(apiQuery),
  });

  // The same period as the tiles above it (it used to be a fixed 26 weeks).
  const trends = useQuery({
    queryKey: ['analytics', 'trends', apiQuery],
    queryFn: () => dashboard.analyticsTrends(apiQuery),
  });

  // A 403 is an answer. It was taking the failure branch and getting a retry
  // button that cannot change it.
  if (schedule.isError && isForbidden(schedule.error)) {
    return (
      <Refused
        heading="This view is not shown to you"
        message="Your account does not have access to the figures behind it. Nothing has been left out of them — they are not yours to see."
      />
    );
  }
  if (schedule.isLoading) return <SkeletonSet surface="table" rows={4} />;
  if (schedule.isError && !schedule.data) {
    return (
      <LoadFailure
        error={schedule.error}
        message="We couldn’t load schedule analytics just now, so read this screen as unknown rather than empty."
        onRetry={() => void schedule.refetch()}
      />
    );
  }
  const d = schedule.data;
  // `return null` rendered a BLANK VIEW — the reader saw a page with nothing on
  // it and no reason given. A query that has not answered is pending.
  if (!d) return <SkeletonSet surface="table" rows={4} />;

  const disclosure = d.disclosure;
  const byWorker = 'byWorker' in d ? d.byWorker : undefined;
  const rosterWithheld = isWithheld(disclosure, 'byWorker');

  /**
   * THE RATES ARE "x of y", AND THE PERCENT IS COMPUTED FROM THAT x AND y.
   * Completion, no-show and not-started are shares of DUE work (not cancelled,
   * still listed, settled or 48 hours past); cancellation is a share of every
   * visit booked (scheduled + cancelled). A suppressed block stays "Hidden".
   */
  const { scheduled, completed, due, cancelled, noShow, unlisted } = d;
  const notStarted =
    completed != null && due != null && noShow != null ? due - completed - noShow : null;
  const share = (part: number | null, whole: number | null): MeasureDisplay =>
    part != null && whole != null
      ? whole > 0
        ? { value: ofLabel(part, whole, count), state: 'value' }
        : {
            value: ofLabel(part, whole, count),
            note: 'Nothing was due in this period, so there is no rate. This is not 0%.',
            state: 'value',
          }
      : measure(null, 'completionRatePct', disclosure, String);
  const booked = scheduled != null && cancelled != null ? scheduled + cancelled : null;

  const open =
    (label: string, key: MetricAboutKey, set: DrillSet, attention?: string | null) => () =>
      setDrill({
        title: `${label}, ${periodRangeLabel(d.period)}`,
        about: METRIC_ABOUT[key],
        attention,
        ask: explainDraft(label.toLowerCase(), askPeriod(d.period)),
        records: { set, from: d.period.from, to: d.period.to, scope: d.scope },
      });
  const notStartedAttention =
    notStarted != null && notStarted > 0
      ? `${count(notStarted)} due ${notStarted === 1 ? 'visit has' : 'visits have'} no outcome in Ritten ` +
        'more than 48 hours after the start. Open them to check whether the visit happened and the ' +
        'record is missing, or it did not happen.'
      : null;

  const columns: Column<ScheduleByWorkerRow>[] = [
    {
      key: 'worker',
      header: 'Worker',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.workerLabel}</span>,
    },
    { key: 'scheduled', header: 'Scheduled', render: (r) => count(r.scheduled), numeric: true },
    { key: 'completed', header: 'Delivered', render: (r) => count(r.completed), numeric: true },
    {
      key: 'completion',
      header: 'Delivered of due',
      render: (r) => ofLabel(r.completed, r.due, count),
      numeric: true,
    },
    { key: 'cancelled', header: 'Cancelled', render: (r) => count(r.cancelled), numeric: true },
    { key: 'noShow', header: 'No-show', render: (r) => count(r.noShow), numeric: true },
  ];

  return (
    <div className="an-stack">
      <DisclosureStrip disclosure={disclosure} />

      <StatGrid>
        <MeasureStat
          label="Scheduled"
          display={measure(scheduled, 'scheduled', disclosure, count)}
          onOpen={open('Scheduled', 'scheduled', 'scheduled')}
        />
        <MeasureStat
          label="Delivered"
          display={measure(completed, 'completed', disclosure, count)}
          onOpen={open('Delivered', 'servicesDelivered', 'delivered')}
        />
        <MeasureStat
          label="Completion (delivered of due)"
          display={share(completed, due)}
          onOpen={open('Completion', 'completion', 'due', notStartedAttention)}
        />
        <MeasureStat
          label="Not started (of due)"
          display={share(notStarted, due)}
          onOpen={open('Not started', 'notStarted', 'not_started', notStartedAttention)}
        />
        <MeasureStat
          label="No-show (of due)"
          display={share(noShow, due)}
          onOpen={open('No-show', 'noShow', 'no_show')}
        />
        <MeasureStat
          label="Cancelled (of booked)"
          display={share(cancelled, booked)}
          onOpen={open('Cancelled', 'cancelled', 'cancelled')}
        />
        <MeasureStat
          label="No longer in Ritten"
          display={measure(unlisted, 'unlisted', disclosure, count)}
          sub="Not cancelled in Ritten; not counted as scheduled"
          onOpen={open(
            'No longer in Ritten',
            'unlisted',
            'unlisted',
            unlisted
              ? 'Check whether these visits were removed in Ritten on purpose. They are not counted as scheduled or as cancelled.'
              : null,
          )}
        />
      </StatGrid>

      {drill ? <DrillPanel key={drill.title} drill={drill} onClose={() => setDrill(null)} /> : null}

      <StalenessStrip staleness={d.staleness} />

      <ScheduleTrendPanel trends={trends} onDrill={setDrill} />

      <Panel
        title="By worker"
        subtitle="visit outcomes per worker — open a worker for their schedule"
      >
        {rosterWithheld || !byWorker ? (
          <DisclosureNote
            heading="Per-worker rows are not shown"
            text={withheldReason('byWorker', 'Per-worker shift outcomes')}
          />
        ) : (
          <DrilldownTable
            rows={byWorker}
            isLoading={false}
            isError={false}
            onRetry={() => void schedule.refetch()}
            columns={columns}
            rowKey={(r) => r.workerId}
            onRowOpen={(r) => navigate(`/people/${encodeURIComponent(r.workerId)}?tab=schedule`)}
            emptyHeading="No visits"
            emptyMessage="Workers appear here once visits are scheduled for them."
          />
        )}
      </Panel>
    </div>
  );
}

function ScheduleTrendPanel({
  trends,
  onDrill,
}: {
  trends: QueryLike<DashboardNS.AnalyticsTrends>;
  onDrill: (drill: Drill) => void;
}) {
  if (trends.isLoading) return <SkeletonSet surface="band" />;
  if (trends.isError && !trends.data) {
    return (
      <TrendFailure
        error={trends.error}
        message="We couldn’t load schedule trends just now."
        onRetry={() => void trends.refetch()}
      />
    );
  }
  const t = trends.data;
  if (!t) return null;
  return (
    <WeeklyPanel
      title="Delivered, due and scheduled"
      axis={t}
      about={METRIC_ABOUT.completion}
      additive
      onDrill={onDrill}
      lines={[
        {
          name: 'Delivered',
          color: 'var(--chart-1)',
          series: t.series.shiftsCompleted,
          set: 'delivered',
        },
        { name: 'Due', color: 'var(--chart-2)', series: t.series.shiftsDue, set: 'due' },
        {
          name: 'Scheduled',
          color: 'var(--chart-context)',
          dashed: true,
          series: t.series.shiftsScheduled,
          set: 'scheduled',
        },
      ]}
    />
  );
}
